import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { Router } from '@angular/router';
import { Group, UserProgress } from 'src/app/shared/models/interfaces';

@Injectable({
  providedIn: 'root',
})
export class GroupsService {
  private selectedGroupSubject = new BehaviorSubject<Group | null>(null);
  selectedGroup$ = this.selectedGroupSubject.asObservable();

  constructor(private http: HttpClient, private router: Router) {}
  /////////////////////////////////////////////////////////////
  // Data fetching methods
  /////////////////////////////////////////////////////////////

  getGroupsForUser(): Observable<Group[]> {
    return this.http.get<Group[]>(`${environment.api}/groups`);
  }

  deleteGroup(groupId: number): Observable<void> {
    return this.http.delete<void>(`${environment.api}/groups/${groupId}`);
  }

  removeStudentFromGroup(groupId: number, studentId: number): Observable<void> {
    return this.http.delete<void>(
      `${environment.api}/groups/${groupId}/user/${studentId}`
    );
  }

  getGroupDetailById(groupId: number): Observable<Group> {
    return this.http
      .get<Group>(`${environment.api}/groups/group/${groupId}`)
      .pipe(tap((group) => this.selectedGroupSubject.next(group)));
  }

  getGroupUserProgressReport(groupId: number): Observable<any> {
    return this.http.get(`${environment.api}/progress-report/group/${groupId}`);
  }

  getGroupParticipants(groupId: number): Observable<UserProgress[]> {
    return this.http.get<UserProgress[]>(
      `${environment.api}/groups/group/participants/${groupId}`
    );
  }

  updateGroup(group: Group): Observable<Group> {
    if (!group.id) {
      throw new Error('Group ID is missing or invalid');
    }
    return this.http
      .put<Group>(`${environment.api}/groups/group/`, group)
      .pipe(
        tap((updatedGroup) => this.selectedGroupSubject.next(updatedGroup))
      );
  }

  createGroup(group: Group): Observable<Group> {
    return this.http
      .post<Group>(`${environment.api}/groups/group/`, group)
      .pipe(tap((newGroup) => this.selectedGroupSubject.next(newGroup)));
  }

  updateAdminRights(groupId: number, userId: number, isAdmin: boolean) {
    return this.http.patch(
      `${environment.api}/groups/group/${groupId}/user/${userId}`,
      {
        isAdmin: isAdmin,
      }
    );
  }

  getUserEmissionsReport(groupId: number, userId: number): Observable<any> {
    return this.http.get(
      `${environment.api}/reports/group/${groupId}/user/${userId}/emissions`
    );
  }

  /////////////////////////////////////////////////////////////
  // Helper methods
  /////////////////////////////////////////////////////////////

  getGroupTrackingStatus(group: Group): 'ongoing' | 'completed' | 'upcoming' {
    const now = new Date();
    const startDate = new Date(group.trackingPeriodStart);
    const finishDate = new Date(group.trackingPeriodFinish);

    if (now < startDate) {
      return 'upcoming';
    } else if (now > finishDate) {
      return 'completed';
    } else {
      return 'ongoing';
    }
  }

  // Set from UI or routing logic
  setSelectedGroup(group: Group): void {
    this.selectedGroupSubject.next(group);
  }

  clearSelectedGroup(): void {
    this.selectedGroupSubject.next(null);
  }

  getSelectedGroup(): Group | null {
    return this.selectedGroupSubject.getValue();
  }

  openCreateOrEditGroupDialog(group?: Group) {
    group ? this.setSelectedGroup(group) : this.clearSelectedGroup();
    const queryParams: any = {
      mode: group ? 'edit' : 'create',
    };

    this.router.navigate(['/group-mask'], { queryParams });
  }

  openGroupsOverview() {
    this.router.navigate(['/group']);
  }
}
